import React, { useCallback, useMemo, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CircularProgress from '@mui/material/CircularProgress';

import { useQuery } from '@apollo/client';
import { useCartMenu } from '@hooks/useCartMenu';
import { useOrderState } from '@hooks/useOrderState';

import {
  fetchOrderCart,
  FetchOrderCartQueryResponse,
} from '@queries/order/queries/fetchOrderCart';

import {
  DrawerCartShoppingCarteBadge,
  DrawerCartShoppingCartIcon,
  DrawerCartSwipeableDrawer,
  DrawerCartAppBar,
  TitleTypography,
  DrawerCartCloseIconButton,
} from './styles/DrawerCart.styles';
import { useEventTracking } from '@hooks/useEventTracking';
import { InteractionEventTypes } from '@validators/Events/Interactions';

const CartIndex = dynamic(
  () => {
    const promise = import('./').then((mod) => mod.CartIndex);
    return promise;
  },
  { ssr: true, loading: () => null },
);

export function DrawerCart() {
  const router = useRouter();
  const { orderUuid } = useOrderState();
  const {
    isOpen: isCartMenuOpen,
    open: openCartMenu,
    close: closeCartMenu,
    isLoading: isCartMenuLoading,
    stopLoading: stopCartMenuLoading,
  } = useCartMenu();

  const { track } = useEventTracking();

  const toggleDrawer = isCartMenuOpen;

  const { data: cartQueryData } = useQuery<FetchOrderCartQueryResponse>(
    fetchOrderCart('cartQuery'),
    {
      variables: {
        params: { uuid: orderUuid },
      },
      fetchPolicy: 'cache-first',
      skip: !orderUuid,
    },
  );

  const cartQuantity = useMemo(() => {
    return cartQueryData?.order?.orderDetails.reduce(
      (acc, { quantity }) => acc + quantity,
      0,
    );
  }, [cartQueryData?.order?.orderDetails]);

  const handleToggleDrawer = useCallback(
    (event?: React.KeyboardEvent | React.MouseEvent, access?: string) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      const products = cartQueryData?.order?.orderDetails.length ?? 0;
      if (toggleDrawer) {
        closeCartMenu();
      } else {
        openCartMenu();
      }
      void track(
        toggleDrawer
          ? InteractionEventTypes.CartClose
          : InteractionEventTypes.CartOpen,
        {
          access: access ?? '',
          products,
        },
      );
    },
    [
      toggleDrawer,
      closeCartMenu,
      openCartMenu,
      cartQueryData?.order?.orderDetails.length,
      track,
    ],
  );

  useEffect(() => {
    stopCartMenuLoading();
  }, [stopCartMenuLoading, cartQuantity]);

  useEffect(() => {
    const openCartFromEmailLink = () => {
      if (router.isReady && router.query.cart === 'open' && !isCartMenuOpen) {
        openCartMenu();
        void track(InteractionEventTypes.CartOpen, {
          access: 'url parameter',
          products: cartQueryData?.order?.orderDetails.length ?? 0,
        });
        void router.replace(
          {
            pathname: router.pathname,
          },
          undefined,
          { shallow: true },
        );
      }
    };
    openCartFromEmailLink();
  }, [
    router,
    isCartMenuOpen,
    openCartMenu,
    track,
    cartQueryData?.order?.orderDetails.length,
  ]);

  return (
    <div>
      <DrawerCartShoppingCartIcon
        onClick={(event: React.MouseEvent) =>
          handleToggleDrawer(event, 'navbar icon')
        }
        hasProducts={cartQuantity > 0}
        size="large"
        aria-label="Carrito de compras (productos agregados)"
      >
        <DrawerCartShoppingCarteBadge
          data-testid="cart-badge"
          badgeContent={
            isCartMenuLoading ? (
              <CircularProgress size={8} thickness={4} color="inherit" />
            ) : (
              cartQuantity
            )
          }
          color="error"
        >
          <ShoppingCartOutlinedIcon aria-label="Icono de carrito de compras" />
        </DrawerCartShoppingCarteBadge>
      </DrawerCartShoppingCartIcon>
      <DrawerCartSwipeableDrawer
        anchor={'right'}
        open={toggleDrawer}
        onClose={(event: React.SyntheticEvent) =>
          handleToggleDrawer(event as React.MouseEvent, 'outside')
        }
        onOpen={(event: React.SyntheticEvent) =>
          handleToggleDrawer(event as React.MouseEvent, 'navbar icon')
        }
      >
        <DrawerCartAppBar>
          <TitleTypography variant="h6">Carro</TitleTypography>
          <DrawerCartCloseIconButton
            onClick={(event: React.MouseEvent) =>
              handleToggleDrawer(event, 'close icon')
            }
            aria-label="Cerrar carrito"
          >
            <CloseRoundedIcon />
          </DrawerCartCloseIconButton>
        </DrawerCartAppBar>
        {toggleDrawer && <CartIndex />}
      </DrawerCartSwipeableDrawer>
    </div>
  );
}
